import type { App } from "@albertine/api-server";
import {
    type QueryClient,
    QueryClientProvider,
    type UseMutationOptions,
    type UseQueryOptions,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { type ClientRequestOptions, hc } from "hono/client";
import type React from "react";
import { createContext, useContext } from "react";
import { z } from "zod";

export { SSEError, type SSEEvent, subscribeToSSE } from "./sse";

type GetAuthTokenOptions = {
    forceRefresh?: boolean;
};

export type GetAuthTokenFn = (options?: GetAuthTokenOptions) => Promise<string | null>;

type WithAuthProps = {
    getFirebaseAuthToken: GetAuthTokenFn;
    getKeycloakToken: GetAuthTokenFn;
    onUnauthorized?: () => void;
};

/**
 * When a Hono client's method is wrapped with this, it will automatically
 * add an `Authorization` header with a Bearer token fetched from the provided
 * token getters on each request.
 */
const withAuthentication =
    (props: WithAuthProps) =>
    <T, O>(method: (opts: T, requestOptions?: ClientRequestOptions) => O) => {
        return async (opts: T, requestOptions?: ClientRequestOptions) => {
            const execute = (accessToken: string | null) => {
                if (accessToken === null) {
                    // Even though we know that the request WILL (should?) fail because
                    // it needs authentication, we still let it go through so that the
                    // error handling in the API client can work as expected and so that
                    // we get logs about it, both on the client and server side.
                    return method(opts, requestOptions);
                }

                const authHeaders = { Authorization: `Bearer ${accessToken}` };
                const headers = { ...requestOptions?.headers, ...authHeaders };
                return method(opts, { ...requestOptions, headers });
            };

            const resolveAccessToken = async (options?: GetAuthTokenOptions) => {
                const keycloakToken = await props.getKeycloakToken(options);
                if (keycloakToken !== null) {
                    return keycloakToken;
                }
                return props.getFirebaseAuthToken(options);
            };

            const firstResponse = await execute(await resolveAccessToken());
            if (!isUnauthorizedResponse(firstResponse)) {
                return firstResponse;
            }

            const refreshedToken = await resolveAccessToken({ forceRefresh: true });
            if (refreshedToken === null) {
                props.onUnauthorized?.();
                return firstResponse;
            }

            const retryResponse = await execute(refreshedToken);
            if (isUnauthorizedResponse(retryResponse)) {
                props.onUnauthorized?.();
            }
            return retryResponse;
        };
    };

const UnauthorizedResponseSchema = z.object({ status: z.literal(401) });

function isUnauthorizedResponse(value: unknown): boolean {
    return UnauthorizedResponseSchema.safeParse(value).success;
}

type CreateApiClientOpts = {
    baseUrl: string;
    getFirebaseAuthToken: GetAuthTokenFn;
    getKeycloakToken: GetAuthTokenFn;
    onUnauthorized?: () => void;
};

const createApiClient = ({
    baseUrl,
    getFirebaseAuthToken,
    getKeycloakToken,
    onUnauthorized,
}: CreateApiClientOpts) => {
    const client = hc<App>(baseUrl);
    const withAuth = withAuthentication({
        getFirebaseAuthToken,
        getKeycloakToken,
        onUnauthorized,
    });
    const withKeycloakAuthOnly = withAuthentication({
        getKeycloakToken,
        getFirebaseAuthToken: async () => Promise.resolve(null),
        onUnauthorized,
    });

    return {
        get: {
            agentProactivity: withAuth(
                client.lmt.members[":memberId"].proactivity.$get,
            ),
            listMemberReferralPromotions: withAuth(
                client.lmt.members[":memberId"]["referral-promotions"].$get,
            ),
            requests: withAuth(client.lmt.requests.$get),
            listTasks: withAuth(client.lmt.tasks.$get),
            dueTasks: withAuth(client.lmt.tasks.due.$get),
            listMembers: withAuth(client.lmt.members.$get),
            listAllMembers: withAuth(client.lmt.members.all.$get),
            getMember: withAuth(client.lmt.members[":memberId"].$get),
            requestRecommendations: withAuth(
                client.lmt.requests[":id"].recommendations.$get,
            ),
            requestProposal: withAuth(client.lmt.requests[":id"].proposal.$get),
            recentConversations: withAuth(client.lmt.conversations.recent.$get),
            membershipTiers: withAuth(client.lmt.memberships.$get),
            marketingPermissions: withAuth(
                client.concierge.v1.members.me["marketing-permissions"].$get,
            ),
            mastercardEligibilityWidgetInitParams:
                client.concierge.v1.mastercard["eligibility-widget-params"].$get,
            incomingMessages: withAuth(client.lmt.messages.incoming.$get),
            dueRequests: withAuth(client.lmt.requests.due.$get),
            dueRequestsCount: withAuth(client.lmt.requests.due.count.$get),
            request: withAuth(client.lmt.requests[":id"].$get),
            booking: withAuth(client.lmt.bookings[":bookingId"].$get),
            bookings: withAuth(client.lmt.bookings.$get),
            // Catalog endpoints
            catalogItemReviews: withAuth(
                client.lmt["catalog-items"][":id"].reviews.$get,
            ),
            catalogItemById: withAuth(client.lmt["catalog-items"][":id"].$get),
            conciergeCatalogItemById: withAuth(
                client.concierge.v1["catalog-items"][":id"].$get,
            ),
            conciergeCatalogItemsByThemeId: withAuth(
                client.concierge.v1.themes[":themeId"]["catalog-items"].$get,
            ),
            conciergeThemeFacets: withAuth(
                client.concierge.v1.themes[":themeId"].facets.$get,
            ),
            catalogItemsSearch: withAuth(client.lmt["catalog-items"].search.$get),
            getLmtFileUpload: withAuth(client.lmt["file-upload"][":id"].$get),
            conciergeDiscoverThemes: withAuth(
                client.concierge.v1.members.me["discover-themes"].$get,
            ),
            lmtCatalogThemeCatalogItems: withAuth(
                client.lmt.themes[":themeId"]["catalog-items"].$get,
            ),
            listThemes: withAuth(client.lmt.themes.$get),
            listServiceHours: withAuth(client.lmt["service-hours"].$get),
            previewMemberDiscoverThemes: withAuth(
                client.lmt.members[":memberId"]["discover-themes"].$get,
            ),
            previewMemberThemeContent: withAuth(
                client.lmt.members[":memberId"]["preview-theme-content"][":themeId"]
                    .$get,
            ),
            theme: withAuth(client.lmt.themes[":themeId"].$get),
            snippets: withAuth(client.concierge.v1.members.me.snippets.$get),
            suppliers: withAuth(client.lmt.suppliers.$get),
            contributionTotals: withAuth(client.lmt.contributions.$get),
            memberPaymentPreferences: withAuth(
                client.lmt.members[":memberId"]["payment-preferences"].$get,
            ),
            memberOnboardingPreferences: withAuth(
                client.lmt.members[":memberId"]["onboarding-preferences"].$get,
            ),
            memberPreferences: withAuth(
                client.lmt.members[":memberId"].preferences.$get,
            ),
            memberStripeAccount: withAuth(
                client.lmt.members[":memberId"]["stripe-account"].$get,
            ),
            getAllAgents: withAuth(client.lmt.agents.$get),
            listAgentTeams: withAuth(client.lmt["agent-teams"].$get),
            listPermissionGroups: withAuth(client.lmt["permission-groups"].$get),
            listMemberGroups: withAuth(client.lmt["member-groups"].$get),
            paymentCustomer: withAuth(
                client.lmt.members[":memberId"]["payment-customer"].$get,
            ),
            findPaymentCustomer: withAuth(client.lmt["payment-customer"].$get),
            paymentMethods: withAuth(
                client.lmt.members[":memberId"]["payment-methods"].$get,
            ),
            bookingPayments: withAuth(client.lmt.bookings[":bookingId"].payments.$get),
            listAllPayments: withAuth(client.lmt.payments.$get),
            mastercardMemberRegistrationStatus: withAuth(
                client.concierge.v1.mastercard["member-registration-status"][
                    ":memberId"
                ].$get,
            ),
            memberRequestExamples: withAuth(
                client.concierge.internal.members.me["request-examples"].$get,
            ),
            memberRequests: withAuth(client.concierge.v1.members.me.requests.$get),
            memberRequest: withAuth(
                client.concierge.v1.members.me.requests[":requestId"].$get,
            ),
            memberProposals: withAuth(client.concierge.v1.members.me.proposals.$get),
            memberProposal: withAuth(
                client.concierge.v1.members.me.proposals[":proposalId"].$get,
            ),
            memberBookings: withAuth(client.concierge.v1.members.me.bookings.$get),
            memberUpcomingBookings: withAuth(
                client.concierge.v1.members.me.bookings.upcoming.$get,
            ),
            memberPastBookings: withAuth(
                client.concierge.v1.members.me.bookings.past.$get,
            ),
            memberBooking: withAuth(
                client.concierge.v1.members.me.bookings[":bookingId"].$get,
            ),
            memberServiceHours: withAuth(
                client.concierge.v1.members.me["service-hours"].$get,
            ),
            getMyMemberSummary: withAuth(client.concierge.v1.members.me.$get),
            getMyConversations: withAuth(
                client.concierge.v1.members.me.conversations.$get,
            ),
            getMyMembership: withAuth(client.concierge.v1.members.me.membership.$get),
            getMyContributionTotal: withAuth(
                client.concierge.v1.members.me.contributions.total.$get,
            ),
            agentAvatarThumbnail: withAuth(
                client.concierge.v1.agents[":agentId"].avatar.thumbnail.$get,
            ),
            memberAvatarThumbnail: withAuth(
                client.concierge.v1.members.me.avatar.thumbnail.$get,
            ),
            // Public — resolving a share link does not require authentication.
            publicShareBookingDetails:
                client.concierge.v1["public-share"].booking[":shareId"].$get,
            publicShareProposalDetails:
                client.concierge.v1["public-share"].proposal[":shareId"].$get,
            generalChatMessages: withAuth(
                client.concierge.v1.members.me["general-chat"].messages.$get,
            ),
            requestChatMessages: withAuth(
                client.concierge.v1.members.me.requests[":requestId"].chat.messages
                    .$get,
            ),
        },
        post: {
            exchangeToken: withKeycloakAuthOnly(
                client.lmt.auth["firebase-tokens"].$post,
            ),
            registerMastercardMember:
                client.concierge.v1.mastercard["register-member"].$post,
            // Agent (LMT) share-link creation — scoped to the agent's tenants server-side.
            createShareLink: withAuth(client.lmt["public-share-links"].$post),
            // Member (Concierge) share-link creation — a member may only share their own resource.
            createMemberShareLink: withAuth(
                client.concierge.v1.members.me["public-share-links"].$post,
            ),
            syncAllThemesToOpenSearch: withAuth(
                client.lmt.developer["index-all-themes-in-open-search"].$post,
            ),
            syncAgentsToKeycloak: withAuth(
                client.lmt.developer["sync-agents-to-keycloak"].$post,
            ),
            ensureAppReviewMembers: withAuth(
                client.lmt.developer["ensure-app-review-members"].$post,
            ),
            syncAllMembersToKeycloak: withAuth(
                client.lmt.developer["sync-all-members-to-keycloak"].$post,
            ),
            syncSingleMemberToKeycloak: withAuth(
                client.lmt.developer["sync-single-member-to-keycloak"][":memberId"]
                    .$post,
            ),
            broadcastMessagesToMembers: withKeycloakAuthOnly(
                client.lmt.developer["broadcast-messages-to-members"].$post,
            ),
            findRevolutMembersByPromoCodes: withAuth(
                client.lmt.developer["find-revolut-members-by-promo-codes"].$post,
            ),
            testTranslation: withAuth(client.lmt.developer["test-translation"].$post),
            closeConfirmedRequests: withAuth(
                client.lmt.developer["close-confirmed-requests"].$post,
            ),
            marketingPermissions: withAuth(
                client.concierge.v1.members.me["marketing-permissions"].$post,
            ),
            memberManageConciergePushNotificationTokens: withAuth(
                client.concierge.v1.members.me.devices["push-notification-tokens"]
                    .$post,
            ),
            markChatPopoverSeen: withAuth(
                client.concierge.v1.members.me.telemetry["chat-popover-seen"].$post,
            ),
            memberSendFeedback: withAuth(client.concierge.v1.members.me.feedback.$post),
            memberSendRequestFeedback: withAuth(
                client.concierge.v1.members.me.requests[":requestId"].feedback.$post,
            ),
            memberSkipRequestFeedback: withAuth(
                client.concierge.v1.members.me.requests[":requestId"].feedback.skip
                    .$post,
            ),
            memberSendBookingFeedback: withAuth(
                client.concierge.v1.members.me.bookings[":bookingId"].feedback.$post,
            ),
            initiateFileUpload: withAuth(client.concierge.v1["file-upload"].$post),
            finalizeFileUpload: withAuth(
                client.concierge.v1["file-upload"][":id"].finalize.$post,
            ),
            sendGeneralChatMessage: withAuth(
                client.concierge.v1.members.me["general-chat"].messages.$post,
            ),
            sendRequestChatMessage: withAuth(
                client.concierge.v1.members.me.requests[":requestId"].chat.messages
                    .$post,
            ),
            agentSendGeneralChatMessage: withAuth(
                client.lmt.members[":memberId"]["general-chat"].messages.$post,
            ),
            agentSendRequestChatMessage: withAuth(
                client.lmt.members[":memberId"].requests[":requestId"].chat.messages
                    .$post,
            ),
            memberReactToMessage: withAuth(
                client.concierge.v1.members.me.conversations.message[":messageId"]
                    .reaction.$put,
            ),
            memberDeleteMessageReaction: withAuth(
                client.concierge.v1.members.me.conversations.message[":messageId"]
                    .reaction.$delete,
            ),
            completeMemberOnboarding: withAuth(
                client.concierge.v1.members.me.onboarding.complete.$post,
            ),
            recordMemberFirstLogin: withAuth(
                client.concierge.v1.members.me["first-login"].$post,
            ),
            memberRequestAccountDeletion: withAuth(
                client.concierge.v1.members.me["request-account-deletion"].$post,
            ),
            markConversationMessagesRead: withAuth(
                client.concierge.v1.members.me.conversations[":conversationId"].read
                    .$post,
            ),
            agentMarkConversationMessagesRead: withAuth(
                client.lmt.members[":memberId"].conversations[":conversationId"].read
                    .$post,
            ),
            agentClearConversationUnseenReactions: withAuth(
                client.lmt.members[":memberId"].conversations[":conversationId"][
                    "agent-unseen-reactions"
                ].clear.$post,
            ),
            generateMemberSummary: withAuth(
                client.lmt.members[":memberId"].conversations[":conversationId"].summary
                    .$post,
            ),
            agentCreateMember: withAuth(client.lmt.members.$post),
            agentValidateNewMember: withAuth(client.lmt.members.validate.$post),
            agentActivateMember: withAuth(
                client.lmt.members[":memberId"].activate.$post,
            ),
            agentCreateAgent: withAuth(client.lmt.agents.$post),
            agentArchiveMember: withAuth(client.lmt.members[":memberId"].archive.$post),
            setMemberOnboardingPhoneCompletionState: withAuth(
                client.lmt.members[":memberId"].onboarding["phone-completion"].$post,
            ),
            createMemberReferralPromotion: withAuth(
                client.lmt.members[":memberId"]["referral-promotions"].$post,
            ),
            createRequest: withAuth(client.lmt.requests.$post),
            archiveRequest: withAuth(client.lmt.requests[":id"].archive.$post),
            unarchiveRequest: withAuth(client.lmt.requests[":id"].unarchive.$post),
            changeRequestStatus: withAuth(client.lmt.requests[":id"].status.$post),
            archiveCatalogItem: withAuth(
                client.lmt["catalog-items"][":id"].archive.$post,
            ),
            unarchiveCatalogItem: withAuth(
                client.lmt["catalog-items"][":id"].unarchive.$post,
            ),
            generateCatalogItemContent: withAuth(
                client.lmt["catalog-items"]["generate-content"].$post,
            ),
            translateCatalogItemContent: withAuth(
                client.lmt["catalog-items"].translations.$post,
            ),
            createCatalogItem: withAuth(client.lmt["catalog-items"].$post),
            initiateLmtFileUpload: withAuth(client.lmt["file-upload"].$post),
            finalizeLmtFileUpload: withAuth(
                client.lmt["file-upload"][":id"].finalize.$post,
            ),
            createTheme: withAuth(client.lmt.themes.$post),
            requestRecommendationsGenerate: withAuth(
                client.lmt.requests[":id"].recommendations.$post,
            ),
            websiteImages: withAuth(client.lmt["crawl-website-images"].$post),
            disableAgent: withAuth(client.lmt.agents[":agentId"].disable.$post),
            enableAgent: withAuth(client.lmt.agents[":agentId"].enable.$post),
            agentEditMemberPersonalInfo: withAuth(
                client.lmt.members[":memberId"]["personal-info"].$post,
            ),
            agentEditMemberContactDetails: withAuth(
                client.lmt.members[":memberId"]["contact-details"].$post,
            ),
            agentEditMemberContributions: withAuth(
                client.lmt.members[":memberId"].contributions.$post,
            ),
            agentLinkFamilyMemberProfile: withAuth(
                client.lmt.members[":memberId"]["link-family-member-profile"].$post,
            ),
            agentUnlinkFamilyMemberProfile: withAuth(
                client.lmt.members[":memberId"]["unlink-family-member-profile"].$post,
            ),
            agentLinkLimitedFamilyMemberProfile: withAuth(
                client.lmt.members[":memberId"]["limited-family-member-profiles"].$post,
            ),
            agentCreateMemberLink: withAuth(
                client.lmt.members[":memberId"].links.$post,
            ),
            agentEditMemberDiningDetails: withAuth(
                client.lmt.members[":memberId"]["dining-details"].$post,
            ),
            agentEditMemberTravelDetails: withAuth(
                client.lmt.members[":memberId"]["travel-details"].$post,
            ),
            agentEditMemberEventsDetails: withAuth(
                client.lmt.members[":memberId"]["events-details"].$post,
            ),
            agentEditMemberWellnessDetails: withAuth(
                client.lmt.members[":memberId"]["wellness-details"].$post,
            ),
            agentEditMemberShoppingDetails: withAuth(
                client.lmt.members[":memberId"]["shopping-details"].$post,
            ),
            agentEditMemberFamilyDetails: withAuth(
                client.lmt.members[":memberId"]["family-details"].$post,
            ),
            createBookingPayment: withAuth(
                client.lmt.bookings[":bookingId"].payments.$post,
            ),
            createBooking: withAuth(client.lmt.bookings.$post),
            createBookingCalendarEvent: withAuth(
                client.lmt.bookings[":bookingId"]["calendar-event"].$post,
            ),
            resendBookingCalendarEvent: withAuth(
                client.lmt.bookings[":bookingId"]["calendar-event"].resend.$post,
            ),
            changeBookingStatus: withAuth(
                client.lmt.bookings[":bookingId"].status.$post,
            ),
            archiveBooking: withAuth(client.lmt.bookings[":bookingId"].archive.$post),
            fetchBookingImages: withAuth(
                client.lmt.bookings[":bookingId"].images.$post,
            ),
            cropBookingImage: withAuth(
                client.lmt.bookings[":bookingId"].images.crop.$post,
            ),
            chargeBookingPayment: withAuth(
                client.lmt.bookings[":bookingId"].payments[":paymentId"].charge.$post,
            ),
            markBookingPaymentAsPaid: withAuth(
                client.lmt.bookings[":bookingId"].payments[":paymentId"]["mark-as-paid"]
                    .$post,
            ),

            refreshContributionTotals: withAuth(
                client.lmt.contributions["refresh-contribution-totals"].$post,
            ),
            createInternalNote: withAuth(
                client.lmt.conversations[":conversationId"]["internal-notes"].$post,
            ),
            completeInternalNote: withAuth(
                client.lmt.conversations[":conversationId"]["internal-notes"][":noteId"]
                    .complete.$post,
            ),
            reopenInternalNote: withAuth(
                client.lmt.conversations[":conversationId"]["internal-notes"][":noteId"]
                    .reopen.$post,
            ),
            sanitizeMessage: withAuth(
                client.lmt.conversations[":conversationId"].messages[":messageId"]
                    .sanitize.$post,
            ),
            assignConversationItemsToRequest: withAuth(
                client.lmt.conversations[":conversationId"]["assign-to-request"].$post,
            ),
            resendConversationMessageNotification: withAuth(
                client.lmt.conversations[":conversationId"].messages[":messageId"][
                    "resend-notification"
                ].$post,
            ),
            copyRequestBookings: withAuth(
                client.lmt.requests[":id"].bookings.copy.$post,
            ),
            cancelMembership: withAuth(
                client.lmt.members[":memberId"].membership.cancel.$post,
            ),
            convertTrialToPaid: withAuth(
                client.lmt.members[":memberId"].membership["convert-to-paid"].$post,
            ),
            extendTrial: withAuth(
                client.lmt.members[":memberId"].membership["extend-trial"].$post,
            ),
        },
        patch: {
            updateRequest: withAuth(client.lmt.requests[":id"].$patch),
            updateAgentPermissionGroup: withAuth(
                client.lmt.agents[":agentId"]["permission-group"].$patch,
            ),
            assignAgentToTeam: withAuth(client.lmt.agents[":agentId"].team.$patch),
            updateAgentProfile: withAuth(client.lmt.agents.me.$patch),
            manageMember: withAuth(client.lmt.members[":memberId"].manage.$patch),
            agentEditLimitedFamilyMemberProfile: withAuth(
                client.lmt.members[":memberId"]["limited-family-member-profiles"][
                    ":profileId"
                ].$patch,
            ),
            agentUpdateMemberLink: withAuth(
                client.lmt.members[":memberId"].links[":linkId"].$patch,
            ),
            agentEditMemberPaymentDetails: withAuth(
                client.lmt.members[":memberId"]["payment-details"].$patch,
            ),
            updateMemberReferralPromotion: withAuth(
                client.lmt.members[":memberId"]["referral-promotions"][":promotionId"]
                    .$patch,
            ),
            editConversationMessage: withAuth(
                client.lmt.conversations[":conversationId"].messages[":messageId"]
                    .$patch,
            ),
            editInternalNote: withAuth(
                client.lmt.conversations[":conversationId"]["internal-notes"][":noteId"]
                    .$patch,
            ),
            changeMyPhoneNumber: withAuth(
                client.concierge.v1.members.me["phone-number"].$patch,
            ),
            changeMyEmail: withAuth(client.concierge.v1.members.me.email.$patch),
            changeMyServiceLanguage: withAuth(
                client.concierge.v1.members.me["service-language"].$patch,
            ),
            updatePersonalInfo: withAuth(
                client.concierge.v1.members.me["personal-info"].$patch,
            ),
            recordDeviceInfo: withAuth(
                client.concierge.v1.members.me.telemetry["device-info"].$patch,
            ),
            recordDeviceLocation: withAuth(
                client.concierge.v1.members.me.telemetry["device-location"].$patch,
            ),
            updateOnboardingGreetingsName: withAuth(
                client.concierge.v1.members.me.onboarding["greetings-name"].$patch,
            ),
            updateOnboardingConciergeExperience: withAuth(
                client.concierge.v1.members.me.onboarding["concierge-experience"]
                    .$patch,
            ),
            updateOnboardingTravelPlans: withAuth(
                client.concierge.v1.members.me.onboarding["travel-plans"].$patch,
            ),
            updateOnboardingDiningPlans: withAuth(
                client.concierge.v1.members.me.onboarding["dining-plans"].$patch,
            ),
            updateOnboardingTopCities: withAuth(
                client.concierge.v1.members.me.onboarding["top-cities"].$patch,
            ),
            updateBooking: withAuth(client.lmt.bookings[":bookingId"].$patch),
            updateTheme: withAuth(client.lmt.themes[":themeId"].$patch),
        },
        put: {
            updateCatalogItem: withAuth(client.lmt["catalog-items"][":id"].$put),
            updateAgent: withAuth(client.lmt.agents[":agentId"].$put),
            syncAgentTeams: withAuth(client.lmt["agent-teams"].$put),
            updateBookingImages: withAuth(
                client.lmt.bookings[":bookingId"].images.$put,
            ),
            updateBookingAttachments: withAuth(
                client.lmt.bookings[":bookingId"].attachments.$put,
            ),
            updateMemberMembership: withAuth(
                client.lmt.members[":memberId"].membership.$put,
            ),
            upsertServiceHours: withAuth(client.lmt["service-hours"][":id"].$put),
            changeMyAvatar: withAuth(client.concierge.v1.members.me.avatar.$put),
        },
        delete: {
            deleteConversationMessage: withAuth(
                client.lmt.conversations[":conversationId"].messages[":messageId"]
                    .$delete,
            ),
            agentUnlinkLimitedFamilyMemberProfile: withAuth(
                client.lmt.members[":memberId"]["limited-family-member-profiles"][
                    ":profileId"
                ].$delete,
            ),
            agentDeleteMemberLink: withAuth(
                client.lmt.members[":memberId"].links[":linkId"].$delete,
            ),
            deleteInternalNote: withAuth(
                client.lmt.conversations[":conversationId"]["internal-notes"][":noteId"]
                    .$delete,
            ),
            deleteTheme: withAuth(client.lmt.themes[":themeId"].$delete),
        },
    };
};

export type ApiClient = ReturnType<typeof createApiClient>;

export const ApiClientContext = createContext<{
    baseUrl: string;
    getFirebaseAuthToken: GetAuthTokenFn;
    getKeycloakToken: GetAuthTokenFn;
    onUnauthorized?: () => void;
} | null>(null);

type ApiClientProviderProps = React.PropsWithChildren<{
    baseUrl: string;
    queryClient: QueryClient;
    /**
     * Function that returns a Promise resolving to a Firebase authentication
     * token, or `null` if the user is not authenticated. Used to automatically
     * add `Authorization` headers to requests that need Firebase authentication.
     * Pass `{ forceRefresh: true }` to force a fresh token after a 401.
     */
    getFirebaseAuthToken: GetAuthTokenFn;
    /**
     * Function that returns a Promise resolving to a Keycloak access token,
     * or `null` if the user is not authenticated via Keycloak. Used for
     * endpoints that require Keycloak authentication (e.g. token exchange).
     * Pass `{ forceRefresh: true }` to force a silent renew after a 401.
     */
    getKeycloakToken: GetAuthTokenFn;
    /**
     * Called when a request returns 401 after a forced token refresh retry.
     * Host apps should clear the session and redirect to login.
     */
    onUnauthorized?: () => void;
    disableDevtools?: boolean;
}>;

export const ApiClientProvider: React.FC<ApiClientProviderProps> = ({
    children,
    baseUrl,
    queryClient,
    getFirebaseAuthToken,
    getKeycloakToken,
    onUnauthorized,
    disableDevtools,
}) => {
    return (
        <ApiClientContext.Provider
            value={{ baseUrl, getFirebaseAuthToken, getKeycloakToken, onUnauthorized }}
        >
            <QueryClientProvider client={queryClient}>
                {children}
                {!disableDevtools && <ReactQueryDevtools initialIsOpen={false} />}
            </QueryClientProvider>
        </ApiClientContext.Provider>
    );
};

/**
 * Custom hook to access the API and query clients. In most cases `useGet` and
 * `usePost` should be used instead of this directly, but this can be a useful
 * escape hatch (especially when there is need to access the API client directly,
 * such as for moving existing systems to get data from through the API).
 *
 * @deprecated Prefer `useGet` and `usePost` instead. This only exists as an escape hatch.
 */
export function useApiClient() {
    const ctx = useContext(ApiClientContext);
    if (!ctx) {
        throw new Error("useApiClient must be used within an ApiClientProvider");
    }

    const { baseUrl, getFirebaseAuthToken, getKeycloakToken, onUnauthorized } = ctx;
    const apiClient = createApiClient({
        baseUrl,
        getFirebaseAuthToken,
        getKeycloakToken,
        onUnauthorized,
    });

    const queryClient = useQueryClient();

    return { apiClient, queryClient };
}

export function useGet<
    const TEntity extends keyof ApiClient["get"],
    TParams extends Parameters<ApiClient["get"][TEntity]>[0],
    TQueryFnData extends Extract<
        Awaited<ReturnType<Awaited<ReturnType<ApiClient["get"][TEntity]>>["json"]>>,
        { success: true }
    >["data"],
    TError extends Extract<
        Awaited<ReturnType<Awaited<ReturnType<ApiClient["get"][TEntity]>>["json"]>>,
        { success: false }
    >,
    TData = TQueryFnData,
>(
    entity: TEntity,
    params: TParams,
    opts?: Omit<
        UseQueryOptions<
            TQueryFnData,
            TError,
            TData,
            Readonly<[entity: TEntity, params: TParams]>
        >,
        "queryKey" | "queryFn"
    >,
) {
    const { apiClient } = useApiClient();

    return useQuery<
        TQueryFnData,
        TError,
        TData,
        Readonly<[entity: TEntity, params: TParams]>
    >({
        queryKey: [entity, params],
        queryFn: async ({ queryKey }) => {
            const [entity, params] = queryKey;
            const fetch = apiClient.get[entity];

            // biome-ignore lint/suspicious/noExplicitAny: Unfortunately it seems that it is not possible to type the params so that TypeScript would be happy about it.
            const resp = await fetch(params as unknown as any);
            const data = await resp.json();

            if (data.warnings !== undefined && data.warnings.length > 0) {
                console.group("API Warnings");
                data.warnings.forEach((warning) => {
                    console.warn(`${resp.url} - ${warning}`, {
                        url: resp.url,
                        warning,
                        queryKey: [entity, params],
                        requestId: data.requestId,
                        status: data.status,
                        success: data.success,
                    });
                });
                console.groupEnd();
            }

            if (!data.success) {
                throw data;
            }

            // For whatever reason, TypeScript doesn't understand that `data.data` is the correct type.
            // Probably because it comes through `(await (await fetch(params)).json())`, where the
            // `params` needs to be cast as `any` to make it work...
            // There is clearly something wonky still with the types of this `useGet` but
            // it now seems to work correctly even when there are multiple endpoints
            // with different payloads.
            return data.data as TQueryFnData;
        },
        ...opts,
    });
}

export function usePost<
    const TEntity extends keyof ApiClient["post"],
    TParams extends Parameters<ApiClient["post"][TEntity]>[0],
    TResponse extends Extract<
        Awaited<ReturnType<Awaited<ReturnType<ApiClient["post"][TEntity]>>["json"]>>,
        { success: true }
    >["data"],
    TError extends Extract<
        Awaited<ReturnType<Awaited<ReturnType<ApiClient["post"][TEntity]>>["json"]>>,
        { success: false }
    >,
>(entity: TEntity, opts?: UseMutationOptions<TResponse, TError, TParams>) {
    const { apiClient } = useApiClient();

    return useMutation<TResponse, TError, TParams>({
        mutationKey: [entity],
        mutationFn: async (params) => {
            const fetch = apiClient.post[entity];

            // biome-ignore lint/suspicious/noExplicitAny: type juggling similar to useGet
            const resp = await fetch(params as unknown as any);
            const data = await resp.json();

            if (data.warnings !== undefined && data.warnings.length > 0) {
                console.group("API Warnings");
                data.warnings.forEach((warning: string) => {
                    console.warn(`${resp.url} - ${warning}`, {
                        url: resp.url,
                        warning,
                        requestId: data.requestId,
                        status: data.status,
                        success: data.success,
                    });
                });
                console.groupEnd();
            }

            if (!data.success) {
                throw data;
            }

            return data.data as TResponse;
        },
        ...opts,
    });
}

export function usePatch<
    const TEntity extends keyof ApiClient["patch"],
    TParams extends Parameters<ApiClient["patch"][TEntity]>[0],
    TResponse extends Extract<
        Awaited<ReturnType<Awaited<ReturnType<ApiClient["patch"][TEntity]>>["json"]>>,
        { success: true }
    >["data"],
    TError extends Extract<
        Awaited<ReturnType<Awaited<ReturnType<ApiClient["patch"][TEntity]>>["json"]>>,
        { success: false }
    >,
>(entity: TEntity, opts?: UseMutationOptions<TResponse, TError, TParams>) {
    const { apiClient } = useApiClient();

    return useMutation<TResponse, TError, TParams>({
        mutationKey: [entity],
        mutationFn: async (params) => {
            const fetch = apiClient.patch[entity];

            // biome-ignore lint/suspicious/noExplicitAny: type juggling similar to useGet
            const resp = await fetch(params as unknown as any);
            const data = await resp.json();

            if (data.warnings !== undefined && data.warnings.length > 0) {
                console.group("API Warnings");
                data.warnings.forEach((warning: string) => {
                    console.warn(`${resp.url} - ${warning}`, {
                        url: resp.url,
                        warning,
                        requestId: data.requestId,
                        status: data.status,
                        success: data.success,
                    });
                });
                console.groupEnd();
            }

            if (!data.success) {
                throw data;
            }

            return data.data as TResponse;
        },
        ...opts,
    });
}

export function usePut<
    const TEntity extends keyof ApiClient["put"],
    TParams extends Parameters<ApiClient["put"][TEntity]>[0],
    TResponse extends Extract<
        Awaited<ReturnType<Awaited<ReturnType<ApiClient["put"][TEntity]>>["json"]>>,
        { success: true }
    >["data"],
    TError extends Extract<
        Awaited<ReturnType<Awaited<ReturnType<ApiClient["put"][TEntity]>>["json"]>>,
        { success: false }
    >,
>(entity: TEntity, opts?: UseMutationOptions<TResponse, TError, TParams>) {
    const { apiClient } = useApiClient();

    return useMutation<TResponse, TError, TParams>({
        mutationKey: [entity],
        mutationFn: async (params) => {
            const fetch = apiClient.put[entity];

            // biome-ignore lint/suspicious/noExplicitAny: type juggling similar to useGet
            const resp = await fetch(params as unknown as any);
            const data = await resp.json();

            if (data.warnings !== undefined && data.warnings.length > 0) {
                console.group("API Warnings");
                data.warnings.forEach((warning: string) => {
                    console.warn(`${resp.url} - ${warning}`, {
                        url: resp.url,
                        warning,
                        requestId: data.requestId,
                        status: data.status,
                        success: data.success,
                    });
                });
                console.groupEnd();
            }

            if (!data.success) {
                throw data;
            }

            return data.data as TResponse;
        },
        ...opts,
    });
}

export function useDelete<
    const TEntity extends keyof ApiClient["delete"],
    TParams extends Parameters<ApiClient["delete"][TEntity]>[0],
    TResponse extends Extract<
        Awaited<ReturnType<Awaited<ReturnType<ApiClient["delete"][TEntity]>>["json"]>>,
        { success: true }
    >["data"],
    TError extends Extract<
        Awaited<ReturnType<Awaited<ReturnType<ApiClient["delete"][TEntity]>>["json"]>>,
        { success: false }
    >,
>(entity: TEntity, opts?: UseMutationOptions<TResponse, TError, TParams>) {
    const { apiClient } = useApiClient();

    return useMutation<TResponse, TError, TParams>({
        mutationKey: [entity],
        mutationFn: async (params) => {
            const fetch = apiClient.delete[entity];

            // biome-ignore lint/suspicious/noExplicitAny: type juggling similar to useGet
            const resp = await fetch(params as unknown as any);
            const data = await resp.json();

            if (data.warnings !== undefined && data.warnings.length > 0) {
                console.group("API Warnings");
                data.warnings.forEach((warning: string) => {
                    console.warn(`${resp.url} - ${warning}`, {
                        url: resp.url,
                        warning,
                        requestId: data.requestId,
                        status: data.status,
                        success: data.success,
                    });
                });
                console.groupEnd();
            }

            if (!data.success) {
                throw data;
            }

            return data.data as TResponse;
        },
        ...opts,
    });
}
